<script setup>
import ReportCard from '../ReportCard.vue';

defineProps({
  title: {
    type: String,
    default: '',
  },
  incomingMessagesLabel: {
    type: String,
    default: '',
  },
  conversationsLabel: {
    type: String,
    default: '',
  },
  resolutionsLabel: {
    type: String,
    default: '',
  },
  averageMessagesPerConversationLabel: {
    type: String,
    default: '',
  },
  resolutionRateLabel: {
    type: String,
    default: '',
  },
  variationLabel: {
    type: String,
    default: '',
  },
  loadingMessage: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  incomingMessages: {
    type: Number,
    default: 0,
  },
  conversations: {
    type: Number,
    default: 0,
  },
  resolutions: {
    type: Number,
    default: 0,
  },
  deltas: {
    type: Object,
    default: () => ({}),
  },
});

const percentage = (value, total) => {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

const averageMessagesPerConversation = (incomingMessages, conversations) => {
  if (!conversations) return '0';

  return (incomingMessages / conversations).toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
  });
};

const deltaClass = delta => {
  return delta >= 0
    ? 'text-green-700 dark:text-green-300'
    : 'text-red-600 dark:text-red-300';
};

const formattedDelta = delta => {
  if (!delta) return '0%';
  return `${delta > 0 ? '+' : ''}${delta}%`;
};
</script>

<template>
  <ReportCard compact :is-loading="isLoading" :loading-message="loadingMessage">
    <h3 class="mb-6 text-xl font-medium text-slate-900 dark:text-slate-50">
      {{ title }}
    </h3>
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_7rem_1fr_7rem_1fr]">
      <div class="flow-step border-woot-200 dark:border-woot-700">
        <span class="flow-icon bg-woot-50 text-woot-700 dark:bg-woot-900">
          <fluent-icon icon="arrow-download" size="20" />
        </span>
        <span>
          <span class="block text-sm text-slate-700 dark:text-slate-200">
            {{ incomingMessagesLabel }}
          </span>
          <strong class="text-2xl text-slate-900 dark:text-slate-50">
            {{ incomingMessages }}
          </strong>
          <span
            class="block mt-1 text-xs font-medium"
            :class="deltaClass(deltas.incomingMessages)"
          >
            {{ formattedDelta(deltas.incomingMessages) }} {{ variationLabel }}
          </span>
        </span>
      </div>
      <div class="flow-connector">
        <span class="flow-line" />
        <span class="flow-rate-pill text-woot-700 dark:text-woot-300">
          <strong>
            {{
              averageMessagesPerConversation(incomingMessages, conversations)
            }}
          </strong>
          <span>{{ averageMessagesPerConversationLabel }}</span>
        </span>
      </div>
      <div class="flow-step border-woot-200 dark:border-woot-700">
        <span class="flow-icon bg-woot-50 text-woot-700 dark:bg-woot-900">
          <fluent-icon icon="chat" size="20" />
        </span>
        <span>
          <span class="block text-sm text-slate-700 dark:text-slate-200">
            {{ conversationsLabel }}
          </span>
          <strong class="text-2xl text-slate-900 dark:text-slate-50">
            {{ conversations }}
          </strong>
          <span
            class="block mt-1 text-xs font-medium"
            :class="deltaClass(deltas.conversations)"
          >
            {{ formattedDelta(deltas.conversations) }} {{ variationLabel }}
          </span>
        </span>
      </div>
      <div class="flow-connector">
        <span class="flow-line" />
        <span class="flow-rate-pill text-green-700 dark:text-green-300">
          <strong>{{ percentage(resolutions, conversations) }}</strong>
          <span>{{ resolutionRateLabel }}</span>
        </span>
      </div>
      <div class="flow-step border-teal-200 dark:border-teal-700">
        <span class="flow-icon bg-teal-50 text-teal-700 dark:bg-teal-900">
          <fluent-icon icon="checkmark-circle" size="20" />
        </span>
        <span>
          <span class="block text-sm text-slate-700 dark:text-slate-200">
            {{ resolutionsLabel }}
          </span>
          <strong class="text-2xl text-slate-900 dark:text-slate-50">
            {{ resolutions }}
          </strong>
          <span
            class="block mt-1 text-xs font-medium"
            :class="deltaClass(deltas.resolutions)"
          >
            {{ formattedDelta(deltas.resolutions) }} {{ variationLabel }}
          </span>
        </span>
      </div>
    </div>
  </ReportCard>
</template>

<style lang="scss" scoped>
.flow-step {
  @apply flex items-center justify-center gap-3 p-4 border border-solid rounded-md bg-slate-25 dark:bg-slate-900;
}

.flow-icon {
  @apply flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full;
}

.flow-connector {
  @apply relative flex flex-col items-center justify-center min-h-[7rem] text-center;
}

.flow-line {
  @apply hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-slate-200 dark:bg-slate-700;
}

.flow-line::after {
  @apply absolute -right-1 -top-1 w-2 h-2 rotate-45 border-t border-r border-solid border-slate-200 dark:border-slate-700 content-[''];
}

.flow-rate-pill {
  @apply relative z-10 flex flex-col items-center justify-center px-2 pt-5 mt-8 text-xs lg:absolute lg:top-1/2 lg:left-1/2 lg:mt-0 lg:-translate-x-1/2 lg:translate-y-4;
  padding-top: 25px;
}

.flow-rate-pill::before {
  @apply absolute top-0 w-px h-4 bg-slate-300 dark:bg-slate-600 content-[''];
}

.flow-rate-pill::after {
  @apply absolute top-4 w-1.5 h-1.5 rounded-full bg-current content-[''];
}

.flow-rate-pill strong {
  @apply text-sm font-medium leading-4;
}

.flow-rate-pill span {
  @apply leading-4 text-slate-500 dark:text-slate-400;
}
</style>
